import { type GalleryConfig, getImagePath } from './data/galleries';

export interface GalleryElements {
  image: HTMLImageElement;
  indicator: HTMLElement;
  prevBtn: HTMLButtonElement;
  nextBtn: HTMLButtonElement;
  thumbs: HTMLElement;
  loading: HTMLElement;
  fullscreenBtn?: HTMLButtonElement | null;
  root: HTMLElement;
}

/** Eager-load only nearby thumbs; rest wait for IntersectionObserver. */
const EAGER_RADIUS = 2;

export function initGallery(config: GalleryConfig, els: GalleryElements): void {
  let current = 1;
  let touchStartX = 0;

  const path = (i: number) => getImagePath(config, i);

  const revealThumb = (thumb: HTMLImageElement) => {
    const src = thumb.dataset.src;
    if (!src || thumb.getAttribute('src')) return;
    thumb.src = src;
  };

  const revealNearby = (center: number) => {
    const nodes = els.thumbs.querySelectorAll<HTMLImageElement>('img.thumbnail');
    nodes.forEach((thumb, i) => {
      const index = i + 1;
      if (Math.abs(index - center) <= EAGER_RADIUS) revealThumb(thumb);
    });
  };

  const setActiveThumb = () => {
    els.thumbs.querySelectorAll('.thumbnail').forEach((thumb, i) => {
      thumb.classList.toggle('active', i + 1 === current);
    });
    const active = els.thumbs.querySelector('.thumbnail.active');
    active?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  };

  const load = (index: number) => {
    current = Math.min(Math.max(index, 1), config.total);
    els.image.classList.remove('is-loaded');
    els.image.alt = `${config.alt ?? 'Pagina'} ${current}`;
    els.image.src = path(current);
    els.image.style.display = 'block';
    els.loading.style.display = 'none';
    els.indicator.textContent = `${current} / ${config.total}`;
    els.prevBtn.disabled = current === 1;
    els.nextBtn.disabled = current === config.total;
    revealNearby(current);
    [current - 1, current + 1].forEach((i) => {
      if (i < 1 || i > config.total) return;
      const pre = new Image();
      pre.src = path(i);
    });
    setActiveThumb();
  };

  els.image.addEventListener('load', () => {
    els.image.classList.add('is-loaded');
  });

  const prev = () => {
    if (current > 1) load(current - 1);
  };
  const next = () => {
    if (current < config.total) load(current + 1);
  };

  els.prevBtn.addEventListener('click', prev);
  els.nextBtn.addEventListener('click', next);

  els.fullscreenBtn?.addEventListener('click', async () => {
    const target = els.root;
    if (!document.fullscreenElement) {
      await target.requestFullscreen?.();
    } else {
      await document.exitFullscreen?.();
    }
  });

  const io =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              revealThumb(entry.target as HTMLImageElement);
              io?.unobserve(entry.target);
            });
          },
          { root: els.thumbs, rootMargin: '80px', threshold: 0.01 },
        )
      : null;

  for (let i = 1; i <= config.total; i++) {
    const thumb = document.createElement('img');
    thumb.dataset.src = path(i);
    thumb.className = 'thumbnail';
    thumb.alt = `Anteprima ${i}`;
    thumb.loading = 'lazy';
    thumb.decoding = 'async';
    thumb.width = 76;
    thumb.height = 56;
    thumb.addEventListener('click', () => load(i));
    els.thumbs.appendChild(thumb);
    io?.observe(thumb);
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft') prev();
    if (e.key === 'ArrowRight') next();
    if (e.key === 'f' || e.key === 'F') els.fullscreenBtn?.click();
  });

  els.root.addEventListener(
    'touchstart',
    (e) => {
      touchStartX = e.changedTouches[0]?.screenX ?? 0;
    },
    { passive: true },
  );

  els.root.addEventListener(
    'touchend',
    (e) => {
      const endX = e.changedTouches[0]?.screenX ?? 0;
      const delta = endX - touchStartX;
      if (Math.abs(delta) < 50) return;
      if (delta > 0) prev();
      else next();
    },
    { passive: true },
  );

  load(1);
}
